import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

type GraphProps = {
  data: any[];
  totalsales: string;   // e.g. total_orders, total_users
  totalhours: string;   // DAILY, WEEKLY, MONTHLY, YEARLY
};

const Graph = ({ data = [], totalsales, totalhours }: GraphProps) => {
  const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const getXAxisKey = () => {
    if (totalhours === "DAILY") return "hour";
    if (totalhours === "WEEKLY") return "day";
    if (totalhours === "MONTHLY") return "date";
    if (totalhours === "YEARLY") return "month";

    if (!data || data.length === 0) return "";
    const firstItem = data[0];
    if ("hour" in firstItem) return "hour";
    if ("day" in firstItem) return "day";
    if ("date" in firstItem) return "date";
    if ("month" in firstItem) return "month";

    return "";
  };

  const getYAxisLabel = () => {
    switch (totalsales) {
      case "total_orders":
        return "Total Orders";
      case "total_users":
        return "Total Users";
      case "total_sellers":
        return "Total Sellers";
      case "total_products":
        return "Total Products";
      case "total_sales":
        return "Total Sales";
      default:
        return totalsales
          ?.replace(/_/g, " ")
          ?.replace(/\b\w/g, (char) => char.toUpperCase()) || "Total";
    }
  };

  const xAxisKey = getXAxisKey();
  const yAxisLabel = getYAxisLabel();

  const sortedData = [...data].sort((a, b) => {
    if (totalhours === "WEEKLY") {
      return dayOrder.indexOf(a[xAxisKey]) - dayOrder.indexOf(b[xAxisKey]);
    }
    if (totalhours === "YEARLY") {
      return monthOrder.indexOf(a[xAxisKey]) - monthOrder.indexOf(b[xAxisKey]);
    }
    if (totalhours === "DAILY") {
      return parseInt(a[xAxisKey]) - parseInt(b[xAxisKey]);
    }
    if (totalhours === "MONTHLY") {
      return parseInt(a[xAxisKey]) - parseInt(b[xAxisKey]);
    }
    return 0;
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            padding: "10px 12px",
            borderRadius: "6px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <p style={{ margin: 0, color: "#555", fontWeight: 500 }}>{label}</p>
          <p style={{ margin: "6px 0 0", color: "#9900CD", fontWeight: 600 }}>
            {yAxisLabel}: {payload[0]?.value ?? 0}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={sortedData}>
        <XAxis
          dataKey={xAxisKey}
          stroke="#2f4050"
          tickFormatter={(value) => {
            if (totalhours === "DAILY") {
              return String(value).padStart(2, "0");
            }
            return value;
          }}
        />

        <YAxis
          allowDecimals={false}
          width={50}
          domain={[0, "auto"]}
          tickFormatter={(value) => Math.round(Number(value)).toString()}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ stroke: "#9900CD", strokeDasharray: "3 3" }}
        />

        <Legend
          formatter={() => (
            <span style={{ color: "#9900CD" }}>{yAxisLabel}</span>
          )}
        />

        <Area
          type="monotone"
          dataKey={totalsales}
          name={yAxisLabel}
          stroke="#243b53"
          strokeWidth={1.2}
          fill="#9900CD"
          fillOpacity={0.15}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default Graph;